import React from "react";
import { Container, Typography, Box, Paper, Divider } from "@mui/material";
import { PRIMARY } from "../theme/theme";

const PrivacyPolicy: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ color: PRIMARY, fontWeight: 700 }}
          >
            Privacy Policy
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            BLUEBERRYBYTES SERVICES FZCO
          </Typography>

          <Typography variant="body2" paragraph sx={{ mb: 3 }}>
            Last updated: April 17, 2025
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="body1" paragraph>
            BLUEBERRYBYTES SERVICES FZCO ("we", "our", or "us") is committed to
            protecting your privacy. This Privacy Policy explains how your
            personal information is collected, used, and disclosed by
            BLUEBERRYBYTES SERVICES FZCO when you use our MicroLearning mobile
            application (the "App").
          </Typography>

          <Typography variant="body1" paragraph>
            By using our App, you agree to the collection and use of information
            in accordance with this policy. If you do not agree with our
            policies and practices, do not download, register with, or use this
            App.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            1. Information We Collect
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            1.1 Personal Information
          </Typography>

          <Typography variant="body1" paragraph>
            We may collect the following types of personal information:
          </Typography>

          <Typography variant="body1" component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Contact information (such as name and email address)</li>
            <li>Account credentials (such as username and password)</li>
            <li>Profile information (such as profile picture and bio)</li>
            <li>
              Payment information (when you subscribe to premium features)
            </li>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            1.2 Usage Data
          </Typography>

          <Typography variant="body1" paragraph>
            We automatically collect certain information when you use our App,
            including:
          </Typography>

          <Typography variant="body1" component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              Device information (such as device type, operating system, and
              browser type)
            </li>
            <li>
              Log data (such as IP address, access times, and app features used)
            </li>
            <li>
              Learning activity (such as lessons completed, quiz scores, and
              time spent)
            </li>
            <li>User preferences and settings</li>
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            2. How We Use Your Information
          </Typography>

          <Typography variant="body1" paragraph>
            We use the information we collect for various purposes, including
            to:
          </Typography>

          <Typography variant="body1" component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Provide, maintain, and improve our App</li>
            <li>Process transactions and send related information</li>
            <li>Send you technical notices, updates, and support messages</li>
            <li>Respond to your comments, questions, and requests</li>
            <li>Personalize your learning experience</li>
            <li>Monitor and analyze trends, usage, and activities</li>
            <li>Detect, prevent, and address technical issues</li>
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            3. Sharing Your Information
          </Typography>

          <Typography variant="body1" paragraph>
            We may share your personal information with:
          </Typography>

          <Typography variant="body1" component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Service providers who perform services on our behalf</li>
            <li>
              Business partners with whom we jointly offer products or services
            </li>
            <li>
              Law enforcement or other third parties when required by law or to
              protect our rights
            </li>
          </Typography>

          <Typography variant="body1" paragraph>
            We do not sell your personal information to third parties.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            4. Data Security
          </Typography>

          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational measures to
            protect the security of your personal information. However, please
            be aware that no method of transmission over the internet or
            electronic storage is 100% secure, and we cannot guarantee absolute
            security.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            5. Your Rights
          </Typography>

          <Typography variant="body1" paragraph>
            Depending on your location, you may have certain rights regarding
            your personal information, such as:
          </Typography>

          <Typography variant="body1" component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Access to your personal information</li>
            <li>Correction of inaccurate or incomplete information</li>
            <li>Deletion of your personal information</li>
            <li>Restriction or objection to processing</li>
            <li>Data portability</li>
          </Typography>

          <Typography variant="body1" paragraph>
            To exercise these rights, please contact us at
            privacy@blueberrybytes.com.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            6. Children's Privacy
          </Typography>

          <Typography variant="body1" paragraph>
            Our App is not intended for children under 13 years of age. We do
            not knowingly collect personal information from children under 13.
            If you are a parent or guardian and believe that your child has
            provided us with personal information, please contact us.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            7. Changes to This Privacy Policy
          </Typography>

          <Typography variant="body1" paragraph>
            We may update our Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last updated" date at the top of this Privacy
            Policy.
          </Typography>

          <Typography
            variant="h5"
            gutterBottom
            sx={{ mt: 4, mb: 2, fontWeight: 600 }}
          >
            8. Contact Us
          </Typography>

          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy, please contact
            us at:
          </Typography>

          <Typography variant="body1" paragraph>
            BLUEBERRYBYTES SERVICES FZCO
            <br />
            Email: privacy@blueberrybytes.com
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy;
